/**
 * Estado da ordenação de uma tabela dos miolos.
 *
 * Papel: guarda qual coluna ordena a tabela e em que direção, e devolve as
 * linhas já ordenadas e a seta de cada cabeçalho.
 *
 * Como usar: a tabela chama `useOrdenacao` com as linhas e com a declaração das
 * colunas, que precisa ser uma constante fora do componente (ver
 * `ordenacao.ts`). O estado vive no componente que chama o hook; para ele
 * sobreviver à troca de aba, como na origem, a aba precisa continuar montada
 * quando escondida — e é o que o `QuadroEmbutido` faz. Cada chamada guarda o
 * seu estado: duas tabelas na mesma tela, cada uma com o seu `useOrdenacao`,
 * ordenam-se sem uma alcançar a outra.
 */
import { useMemo, useState } from 'react';

import {
  type ColunaOrdenavel,
  type EstadoDeOrdenacao,
  type RegraDeOrdenacao,
  SEM_ORDENACAO,
  ordenarLinhas,
  proximoEstado,
} from './ordenacao';

/** A seta que um cabeçalho mostra: só a coluna ativa tem uma. */
export type SetaDeOrdenacao = '▲' | '▼' | null;

/**
 * Ordena uma tabela por clique no cabeçalho.
 *
 * Uma lista nova — depois de gravar ou excluir — é reordenada pela coluna
 * ativa, sem que a ordenação volte ao começo.
 *
 * @param linhas - As linhas na ordem de chegada.
 * @param colunas - A declaração das colunas ordenáveis, constante fora do
 *   componente.
 * @param regra - `'alternada'` (o padrão) ou `'com-retorno'`, que volta à ordem
 *   de chegada no terceiro clique e põe o vazio sempre no fim; ver
 *   `RegraDeOrdenacao`.
 * @returns As linhas ordenadas, `alternar` para o clique num cabeçalho e `seta`
 *   para o que cada cabeçalho mostra.
 */
export function useOrdenacao<L>(
  linhas: readonly L[],
  colunas: Readonly<Record<string, ColunaOrdenavel<L>>>,
  regra: RegraDeOrdenacao = 'alternada',
) {
  const [estado, setEstado] = useState<EstadoDeOrdenacao>(SEM_ORDENACAO);
  const ordenadas = useMemo(
    () => ordenarLinhas(linhas, colunas, estado, regra),
    [linhas, colunas, estado, regra],
  );

  /**
   * Registra o clique no cabeçalho de uma coluna.
   *
   * @param coluna - A chave da coluna, a mesma da declaração.
   */
  function alternar(coluna: string) {
    setEstado((atual) => proximoEstado(atual, coluna, regra));
  }

  /**
   * A seta do cabeçalho de uma coluna.
   *
   * @param coluna - A chave da coluna.
   * @returns "▲" na crescente, "▼" na decrescente, `null` se não é a ativa.
   */
  function seta(coluna: string): SetaDeOrdenacao {
    if (estado.coluna !== coluna) return null;
    return estado.direcao === 'crescente' ? '▲' : '▼';
  }

  return { linhas: ordenadas, alternar, seta };
}
